import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  AgentChatResponsePart,
  ConversationMessage,
  ConversationMessagePayload,
  sanitizeMessageForDisplay,
} from '../../components/SignupFlow/AiCompanion/conversation';

const COPY_FEEDBACK_DURATION_MS = 1500;

const useConversationTranscript = () => {
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesRef = useRef<ConversationMessage[]>([]);
  const messageSequenceRef = useRef(0);
  const sessionSequenceRef = useRef(0);
  const pendingUserMessagesRef = useRef<string[]>([]);

  const nextMessageId = useCallback((prefix: string) => {
    messageSequenceRef.current += 1;
    return `${prefix}-${messageSequenceRef.current}`;
  }, []);

  const handleMessage = useCallback(({
    event_id: eventId,
    message,
    source,
  }: ConversationMessagePayload) => {
    const receivedMessage = message.trim();

    if (!receivedMessage) {
      return;
    }

    if (source === 'user') {
      const pendingIndex = pendingUserMessagesRef.current.indexOf(
        receivedMessage,
      );

      if (pendingIndex >= 0) {
        pendingUserMessagesRef.current.splice(pendingIndex, 1);
        return;
      }
    }

    const role = source === 'user' ? 'user' : 'assistant';
    const eventMessageId = eventId === undefined
      ? null
      : `agent-${sessionSequenceRef.current}-${role}-${eventId}`;

    setMessages((currentMessages) => {
      if (eventMessageId) {
        const eventMessageIndex = currentMessages.findIndex(({ id }) =>
          id === eventMessageId);

        if (eventMessageIndex >= 0) {
          return currentMessages.map((currentMessage, index) =>
            (index === eventMessageIndex
              ? {
                ...currentMessage,
                message: receivedMessage,
                isStreaming: false,
              }
              : currentMessage));
        }
      }

      const lastMessage = currentMessages[currentMessages.length - 1];
      const isDuplicate = lastMessage?.role === role
        && lastMessage.message === receivedMessage;

      if (isDuplicate) {
        return currentMessages;
      }

      return [
        ...currentMessages,
        {
          id: eventMessageId ?? nextMessageId(`sdk-${role}`),
          message: receivedMessage,
          role,
        },
      ];
    });
  }, [nextMessageId]);

  const handleAgentChatResponsePart = useCallback(({
    event_id: eventId,
    text,
    type,
  }: AgentChatResponsePart) => {
    const messageId = `agent-${sessionSequenceRef.current}-assistant-${eventId}`;

    setMessages((currentMessages) => {
      const messageIndex = currentMessages.findIndex(({ id }) =>
        id === messageId);

      if (messageIndex < 0) {
        if (type === 'stop' && !text) {
          return currentMessages;
        }

        return [
          ...currentMessages,
          {
            id: messageId,
            message: text,
            role: 'assistant',
            isStreaming: type !== 'stop',
          },
        ];
      }

      return currentMessages.map((currentMessage, index) => {
        if (index !== messageIndex) {
          return currentMessage;
        }

        let updatedText = currentMessage.message;

        if (type === 'start') {
          updatedText = text;
        } else if (type === 'delta') {
          updatedText += text;
        } else if (!updatedText) {
          updatedText = text;
        }

        return {
          ...currentMessage,
          message: updatedText,
          isStreaming: type !== 'stop',
        };
      });
    });
  }, []);

  const beginSession = useCallback(() => {
    sessionSequenceRef.current += 1;
  }, []);

  const queuePendingUserMessage = useCallback((message: string) => {
    pendingUserMessagesRef.current.push(message);
  }, []);

  const discardPendingUserMessage = useCallback((message: string) => {
    const pendingIndex = pendingUserMessagesRef.current.lastIndexOf(message);

    if (pendingIndex >= 0) {
      pendingUserMessagesRef.current.splice(pendingIndex, 1);
    }
  }, []);

  const appendUserMessage = useCallback((message: string) => {
    setMessages((currentMessages) =>
      [
        ...currentMessages,
        {
          id: nextMessageId('local-user'),
          message,
          role: 'user',
        },
      ]);
  }, [nextMessageId]);

  const handleCopyMessage = useCallback(async (
    item: ConversationMessage,
  ) => {
    try {
      await navigator.clipboard.writeText(
        sanitizeMessageForDisplay(item.message),
      );
      setCopiedMessageId(item.id);
      window.setTimeout(
        () =>
          setCopiedMessageId(null),
        COPY_FEEDBACK_DURATION_MS,
      );
    } catch {
      setCopiedMessageId(null);
    }
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  return {
    appendUserMessage,
    beginSession,
    copiedMessageId,
    discardPendingUserMessage,
    handleAgentChatResponsePart,
    handleCopyMessage,
    handleMessage,
    messages,
    messagesEndRef,
    messagesRef,
    queuePendingUserMessage,
  };
};

export type ConversationTranscript = ReturnType<
  typeof useConversationTranscript
>;

export default useConversationTranscript;
